import {
  FieldValidation,
  formatDataSource,
  getActionLabel,
  getSeverityColor,
  getValidationStatusColor,
  getValidationStatusLabel,
} from "@/types/validation";
import { createPortal } from "react-dom";

interface ValidationPopoverProps {
  /** Field name for display */
  fieldName: string;

  /** Validation metadata */
  validation: FieldValidation;

  /** Current field value */
  currentValue: string | number | boolean | null;

  /** Confidence score (0-1) */
  confidence: number;

  /** Callback when popover should close */
  onClose: () => void;
}

/**
 * Popover that displays AI validation reasoning, confidence, data sources, and discrepancies
 */
export function ValidationPopover({
  fieldName,
  validation,
  currentValue,
  confidence,
  onClose,
}: ValidationPopoverProps) {
  const colors = getValidationStatusColor(validation.status);
  const confidencePercent = Math.round(confidence * 100);

  const popoverContent = (
    <>
      {/* Backdrop for mobile - clicking closes popover */}
      <div className="fixed inset-0 z-[9998] lg:hidden" onClick={onClose} aria-hidden="true" />

      {/* Popover content - use fixed positioning to escape stacking context */}
      <div
        className="fixed z-[9999] w-80 sm:w-96 shadow-2xl"
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
        onMouseLeave={onClose}
      >
        <div
          className={`rounded-lg shadow-2xl border ${colors.border} ${colors.bg} p-4 space-y-3 animate-in fade-in slide-in-from-top-2 duration-200`}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{fieldName}</h3>
              <p className={`text-xs font-medium mt-0.5 ${colors.text}`}>
                {getValidationStatusLabel(validation.status)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Current Value & Confidence */}
          <div className="bg-white dark:bg-slate-800 rounded-md p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
                Extracted Value:
              </span>
              <span className="text-sm font-semibold text-slate-900 dark:text-white">
                {currentValue !== null && currentValue !== undefined ? String(currentValue) : "N/A"}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
                AI Confidence:
              </span>
              <div className="flex items-center gap-2">
                <div className="w-20 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      confidencePercent >= 90
                        ? "bg-green-500"
                        : confidencePercent >= 75
                          ? "bg-yellow-500"
                          : "bg-red-500"
                    }`}
                    style={{ width: `${confidencePercent}%` }}
                  />
                </div>
                <span className="text-sm font-bold text-slate-900 dark:text-white">
                  {confidencePercent}%
                </span>
              </div>
            </div>
          </div>

          {/* Suggested Correction Value Comparison */}
          {(validation.status === "suggested_add" ||
            validation.status === "suggested_update" ||
            validation.status === "suggested_review") && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3 space-y-2">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold text-blue-800 dark:text-blue-300">
                  Suggested Correction
                </h4>
                {validation.severity && (
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getSeverityColor(validation.severity).badge}`}
                  >
                    {validation.severity}
                  </span>
                )}
              </div>

              {validation.suggested_action && (
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-blue-700 dark:text-blue-400">
                    Action:
                  </span>
                  <span className="text-xs font-semibold text-blue-900 dark:text-blue-200">
                    {getActionLabel(validation.suggested_action)}
                  </span>
                </div>
              )}

              {validation.extracted_value !== undefined && (
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-blue-700 dark:text-blue-400">
                    Document Value:
                  </span>
                  <span className="text-xs font-semibold text-blue-900 dark:text-blue-200">
                    {validation.extracted_value !== null
                      ? String(validation.extracted_value)
                      : "Not found"}
                  </span>
                </div>
              )}

              {validation.existing_value !== undefined && (
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-blue-700 dark:text-blue-400">
                    System Value:
                  </span>
                  <span className="text-xs font-semibold text-blue-900 dark:text-blue-200">
                    {validation.existing_value !== null
                      ? String(validation.existing_value)
                      : "Not in system"}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Auto-Correction Details */}
          {validation.status === "auto_corrected" && validation.original_value !== undefined && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-3 space-y-2">
              <h4 className="text-xs font-semibold text-green-800 dark:text-green-300 mb-2">
                Auto-Corrected
              </h4>

              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-green-700 dark:text-green-400">
                  Original Value:
                </span>
                <span className="text-xs line-through text-green-600 dark:text-green-400">
                  {validation.original_value !== null ? String(validation.original_value) : "Empty"}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-green-700 dark:text-green-400">
                  Corrected To:
                </span>
                <span className="text-xs font-semibold text-green-900 dark:text-green-200">
                  {validation.corrected_value !== null ? String(validation.corrected_value) : "N/A"}
                </span>
              </div>
            </div>
          )}

          {/* AI Reasoning */}
          {validation.reasoning && (
            <div className="bg-white dark:bg-slate-800 rounded-md p-3">
              <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                AI Reasoning
              </h4>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                {validation.reasoning}
              </p>
            </div>
          )}

          {/* Data Sources */}
          {validation.data_sources && validation.data_sources.length > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-md p-3">
              <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Data Sources
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {validation.data_sources.map((source) => (
                  <span
                    key={source}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                  >
                    {formatDataSource(source)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Discrepancies */}
          {validation.discrepancies && validation.discrepancies.length > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-md p-3">
              <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Discrepancies Found
              </h4>
              <div className="space-y-2">
                {validation.discrepancies.map((discrepancy, index) => (
                  <div
                    key={index}
                    className="bg-slate-50 dark:bg-slate-900/50 rounded p-2 space-y-1"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                        {formatDataSource(discrepancy.source)}:
                      </span>
                      <span className="text-xs font-semibold text-slate-900 dark:text-white">
                        {discrepancy.value !== null && discrepancy.value !== undefined
                          ? String(discrepancy.value)
                          : "Not found"}
                      </span>
                    </div>
                    {discrepancy.reason && (
                      <p className="text-xs text-slate-600 dark:text-slate-400 italic">
                        {discrepancy.reason}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Customer Patterns */}
          {validation.customer_patterns && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-3">
              <div className="flex items-start gap-2">
                <svg
                  className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="flex-1">
                  <h4 className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-1">
                    Customer Pattern Detected
                  </h4>
                  <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
                    {validation.customer_patterns}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  // Render with portal to escape stacking context
  return createPortal(popoverContent, document.body);
}
